import { AGE_VERIFICATION } from '../utils/constants';

// Site-wide self-declaration age gate. The dialog is opened modally so the
// page behind is inert and focus stays inside; Escape and any other close
// path re-open it until the visitor confirms.

const isVerified = () => {
  try {
    return localStorage.getItem(AGE_VERIFICATION.STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistVerified = () => {
  try {
    localStorage.setItem(AGE_VERIFICATION.STORAGE_KEY, 'true');
  } catch (e) {
    console.warn('Age verification could not be saved', e);
  }
};

// Returns the age in whole years, or null when the parts don't form a real
// date (e.g. 31 February). Someone turning the limit age today passes.
const getAge = (day, month, year) => {
  const birthDate = new Date(year, month - 1, day);
  const isRealDate = birthDate.getFullYear() === year
    && birthDate.getMonth() === month - 1
    && birthDate.getDate() === day;
  if (!isRealDate) return null;

  const today = new Date();
  const hasHadBirthday = today.getMonth() > birthDate.getMonth()
    || (today.getMonth() === birthDate.getMonth() && today.getDate() >= day);
  return today.getFullYear() - year - (hasHadBirthday ? 0 : 1);
};

// Reveals the matching message and points the selects at it, so the error is
// announced now and read again when a select regains focus. Only the empty
// selects are flagged when parts are missing; when every part is filled the
// date as a whole is wrong, so all three are.
const showError = (formEl, errorType) => {
  let activeErrorId = '';
  formEl.querySelectorAll('[data-age-verification-error]').forEach((errorEl) => {
    const isActive = errorEl.dataset.ageVerificationError === errorType;
    errorEl.hidden = !isActive;
    if (isActive) activeErrorId = errorEl.id;
  });

  const selectEls = [...formEl.querySelectorAll('select')];
  const hasEmptySelect = selectEls.some((selectEl) => !selectEl.value);
  selectEls.forEach((selectEl) => {
    const isInvalid = !hasEmptySelect || !selectEl.value;
    // aria-invalid is enumerated: an empty value (toggleAttribute) reads as false
    if (isInvalid) {
      selectEl.setAttribute('aria-invalid', 'true');
      selectEl.setAttribute('aria-describedby', activeErrorId);
    } else {
      selectEl.removeAttribute('aria-invalid');
      selectEl.removeAttribute('aria-describedby');
    }
  });
};

const isDateValid = (formEl, ageLimit) => {
  const day = Number(formEl.querySelector('[data-age-verification-day]')?.value);
  const month = Number(formEl.querySelector('[data-age-verification-month]')?.value);
  const year = Number(formEl.querySelector('[data-age-verification-year]')?.value);
  if (!day || !month || !year) {
    showError(formEl, AGE_VERIFICATION.ERROR_INCOMPLETE);
    return false;
  }

  const age = getAge(day, month, year);
  if (age === null) {
    showError(formEl, AGE_VERIFICATION.ERROR_INCOMPLETE);
    return false;
  }
  if (age < ageLimit) {
    showError(formEl, AGE_VERIFICATION.ERROR_AGE);
    return false;
  }
  return true;
};

const openGate = (dialogEl, formEl, isDesignMode) => {
  const isFullDate = dialogEl.dataset.ageVerificationMode === AGE_VERIFICATION.MODE_FULL_DATE;
  const ageLimit = Number(dialogEl.dataset.ageVerificationLimit);
  let isConfirmed = false;

  formEl.addEventListener('submit', (event) => {
    event.preventDefault();
    if (isFullDate && !isDateValid(formEl, ageLimit)) return;
    isConfirmed = true;
    // In the theme editor the choice isn't saved, so the merchant can preview again
    if (!isDesignMode) persistVerified();
    dialogEl.close();
  });

  dialogEl.addEventListener('cancel', (event) => event.preventDefault());
  // Live, not defensive: Chrome only honours preventDefault on the first
  // Escape; a second rapid press closes the dialog anyway, so re-open it.
  dialogEl.addEventListener('close', () => {
    if (!isConfirmed) dialogEl.showModal();
  });
  dialogEl.showModal();
};

const initAgeVerification = () => {
  const dialogEl = document.querySelector('[data-age-verification]');
  if (!dialogEl || dialogEl.dataset.ageVerificationInit) return;
  dialogEl.dataset.ageVerificationInit = 'true';

  const formEl = dialogEl.querySelector('[data-age-verification-form]');
  if (!formEl) return;

  const isDesignMode = Boolean(window.Shopify?.designMode);
  if (isVerified() && !isDesignMode) return;
  openGate(dialogEl, formEl, isDesignMode);
};

export default initAgeVerification;
